import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { productsApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { Search, Lock } from 'lucide-react';
import { Stars } from '../components/ProductReviews';

const BAMBINO_IMAGES = [
  'https://static.prod-images.emergentagent.com/jobs/af49a006-9be0-463b-96a6-a9914972e5ea/images/1a0c11936f2544bc5d4a04e279b191267ad80e1cf74a48ed6e61bb38f5239ca9.jpeg',
  'https://static.prod-images.emergentagent.com/jobs/af49a006-9be0-463b-96a6-a9914972e5ea/images/2418f9a81feee1ed2c53ba60bd0ee1ea4f11665f6a9cf27bf75ff424a166a9f4.jpeg',
  'https://static.prod-images.emergentagent.com/jobs/af49a006-9be0-463b-96a6-a9914972e5ea/images/e8fbb22b5d1ebf05b0f46519511452f0c271510fc9fc70fa32110c1ebb629501.jpeg',
];

export default function ShopPage() {
  const { isAuthenticated } = useAuth();
  const { t, tField } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const category = searchParams.get('category') || '';

  const categories = [
    { value: '', label: { it: 'Tutti', en: 'All' } },
    { value: 'uomo', label: { it: 'Uomo', en: 'Men' } },
    { value: 'donna', label: { it: 'Donna', en: 'Women' } },
    { value: 'bambino', label: { it: 'Bambino', en: 'Kids' } },
    { value: 'accessori', label: { it: 'Accessori', en: 'Accessories' } },
  ];

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = {};
        if (category) params.category = category;
        if (searchTerm) params.search = searchTerm;
        const response = await productsApi.getAll(params);
        setProducts(response.data.products);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [category, searchTerm]);

  const handleCategoryChange = (newCategory) => {
    const params = new URLSearchParams(searchParams);
    if (newCategory) {
      params.set('category', newCategory);
    } else {
      params.delete('category');
    }
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSearchParams({});
    setSearchTerm('');
  };

  const lockedOverlayText = isAuthenticated
    ? { it: 'In anteprima per te dal 15/11/2026', en: 'Preview for you from 15/11/2026' }
    : { it: 'Iscriviti per scoprire in anteprima la collezione', en: 'Sign up to preview the collection' };

  const lockedPriceText = isAuthenticated
    ? { it: 'Anteprima dal 15/11/2026', en: 'Preview from 15/11/2026' }
    : { it: 'Riservato agli iscritti', en: 'Members only' };

  const bambinoPlaceholders = [
    { name: { it: 'T-Shirt in Cotone Bio', en: 'Organic Cotton T-Shirt' }, img: BAMBINO_IMAGES[0] },
    { name: { it: 'Maglione in Lana Merino', en: 'Merino Wool Sweater' }, img: BAMBINO_IMAGES[1] },
    { name: { it: 'Salopette in Lino Bio', en: 'Organic Linen Overalls' }, img: BAMBINO_IMAGES[2] },
  ];

  const categoryLabel = (cat) => {
    const map = {
      uomo: { it: 'uomo', en: 'men' },
      donna: { it: 'donna', en: 'women' },
      bambino: { it: 'bambino', en: 'kids' },
      accessori: { it: 'accessori', en: 'accessories' },
    };
    return map[cat] ? t(map[cat]) : cat;
  };

  const LockedImage = ({ src, alt, testid }) => (
    <div className="relative aspect-[3/4] bg-[#F2EFE9] mb-4 overflow-hidden">
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover blur-lg scale-110"
      />
      <div
        data-testid={testid}
        className="absolute inset-0 bg-black/30 flex flex-col items-center justify-center text-center px-6"
      >
        <Lock className="w-6 h-6 text-white mb-4" />
        <p className="text-white text-sm font-medium uppercase tracking-widest leading-relaxed">
          {t(lockedOverlayText)}
        </p>
      </div>
    </div>
  );

  const activeCategory = categories.find((c) => c.value === category);

  return (
    <div className="min-h-screen pt-24 pb-16 px-6 md:px-12 lg:px-24">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-12">
          <p className="overline mb-4">{t({ it: 'Collezione', en: 'Collection' })}</p>
          <h1 className="text-4xl md:text-5xl font-light tracking-tight">
            {activeCategory && activeCategory.value
              ? t(activeCategory.label)
              : t({ it: 'Tutti i Prodotti', en: 'All Products' })}
          </h1>
        </div>

        {/* Filters Bar */}
        <div className="flex flex-wrap items-center gap-4 mb-12">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat.value}
                onClick={() => handleCategoryChange(cat.value)}
                data-testid={`filter-category-${cat.value || 'all'}`}
                className={`px-5 py-2 text-sm transition-colors ${
                  category === cat.value
                    ? 'bg-[#1C1917] text-white'
                    : 'bg-white border border-[#E5E0D8] text-[#1C1917] hover:border-[#1C1917]'
                }`}
              >
                {t(cat.label)}
              </button>
            ))}
          </div>
          <div className="relative ml-auto">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#57534E]" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              data-testid="shop-search-input"
              placeholder={t({ it: 'Cerca...', en: 'Search...' })}
              className="pl-11 pr-4 py-2 border border-[#E5E0D8] focus:border-[#4A5D23] transition-colors text-sm w-48 md:w-64"
            />
          </div>
        </div>

        {/* Products Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[3/4] bg-[#E5E0D8] mb-4" />
                <div className="h-4 bg-[#E5E0D8] w-3/4 mb-2" />
                <div className="h-4 bg-[#E5E0D8] w-1/2" />
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          category === 'bambino' ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-testid="bambino-coming-soon">
              {bambinoPlaceholders.map((item, idx) => {
                const CardInner = (
                  <>
                    <LockedImage src={item.img} alt={t(item.name)} testid={`bambino-locked-${idx}`} />
                    <p className="text-xs uppercase tracking-wider text-[#57534E] mb-1">
                      {t({ it: 'bambino', en: 'kids' })}
                    </p>
                    <h3 className="font-medium mb-1">{t(item.name)}</h3>
                    <p className="text-[#4A5D23] font-medium text-sm">{t(lockedPriceText)}</p>
                  </>
                );
                return isAuthenticated ? (
                  <div key={idx} data-testid={`bambino-card-${idx}`} className="group">{CardInner}</div>
                ) : (
                  <Link key={idx} data-testid={`bambino-card-${idx}`} to="/register" className="group card-hover">{CardInner}</Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-[#57534E] text-lg">{t({ it: 'Nessun prodotto trovato', en: 'No products found' })}</p>
              <button
                onClick={clearFilters}
                className="mt-4 text-[#4A5D23] font-medium hover:underline"
              >
                {t({ it: 'Rimuovi tutti i filtri', en: 'Clear all filters' })}
              </button>
            </div>
          )
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
            {products.map((product, idx) => {
              const CardInner = (
                <>
                  <LockedImage
                    src={product.images[0] || 'https://via.placeholder.com/400x500'}
                    alt={product.name}
                    testid={`locked-overlay-${idx}`}
                  />
                  <p className="text-xs uppercase tracking-wider text-[#57534E] mb-1">
                    {categoryLabel(product.category)}
                  </p>
                  <h3 className="font-medium mb-1 group-hover:text-[#4A5D23] transition-colors">
                    {tField(product, 'name')}
                  </h3>
                  {product.rating_count > 0 && (
                    <div className="flex items-center gap-1.5 mb-1" data-testid={`card-rating-${idx}`}>
                      <Stars rating={product.rating_avg} size="w-3.5 h-3.5" />
                      <span className="text-xs text-[#57534E]">({product.rating_count})</span>
                    </div>
                  )}
                  <p className="text-[#4A5D23] font-medium text-sm">{t(lockedPriceText)}</p>
                </>
              );

              return isAuthenticated ? (
                <div key={product.product_id} data-testid={`product-card-${idx}`} className="group">
                  {CardInner}
                </div>
              ) : (
                <Link
                  key={product.product_id}
                  to="/register"
                  data-testid={`product-card-${idx}`}
                  className="group card-hover"
                >
                  {CardInner}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
